import styled from 'styled-components';
import { PH } from './theme';

export const StyledAboutTab = styled.div`
  font-size: ${PH.fontSizeBase};
  line-height: ${PH.lineHeightBody};
  color: ${PH.text};

  .about-alert {
    margin-bottom: 20px;
  }

  .about-layout {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: stretch;

    @media (min-width: 960px) {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: flex-start;
      gap: 20px;
      /* Let onboarding stay left; integration details hug the right (margin-left: auto on sidebar) */
      justify-content: flex-start;
    }
  }

  .about-main {
    min-width: 0;
    flex: 1 1 auto;

    @media (min-width: 960px) {
      /* Don’t grow into the middle — keeps the details card against the right edge */
      flex: 0 1 auto;
      max-width: 760px;
    }
  }

  .about-sidebar {
    flex: 0 0 auto;
    width: 100%;

    @media (min-width: 960px) {
      position: sticky;
      top: 24px;
      /* Wide enough for full UUID on one line; auto margin eats remaining row space → sits on the right */
      width: min(500px, 100%);
      min-width: 440px;
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  .about-section {
    margin-bottom: 0;
  }

  .about-section-title {
    font-size: ${PH.fontSizeLg};
    font-weight: 700;
    color: ${PH.text};
    margin-bottom: 12px;
    letter-spacing: -0.01em;
  }

  .about-steps-header {
    margin-bottom: 16px;
  }

  .about-progress-wrap {
    margin-top: 8px;
  }

  .about-progress-text {
    display: block;
    font-size: ${PH.fontSizeSm};
    font-weight: 600;
    color: ${PH.textLabel};
    margin-bottom: 6px;
  }

  .about-progress-bar {
    .ant-progress-bg {
      border-radius: 2px;
    }
  }

  .about-all-set {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    margin-bottom: 20px;
    background: ${PH.successBg};
    border: 1px solid rgba(14, 98, 63, 0.2);
    border-radius: 8px;
    font-size: ${PH.fontSizeBase};
    font-weight: 600;
    color: ${PH.success};
  }

  .about-all-set-icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  .about-section-intro {
    color: ${PH.textMuted};
    margin-bottom: 16px;
    font-size: ${PH.fontSizeSm};
    line-height: 1.55;
  }

  .about-details-card {
    border: 1px solid ${PH.border};
    border-radius: 8px;
    overflow: hidden;
    background: ${PH.surface};
  }

  .about-descriptions {
    .ant-descriptions-item-label {
      font-size: ${PH.fontSizeSm};
      font-weight: 600;
      color: ${PH.textLabel};
      background: ${PH.surfaceMuted};
      width: 108px;
      padding: 10px 12px;
    }

    .ant-descriptions-item-content {
      font-size: ${PH.fontSizeBase};
      color: ${PH.text};
      padding: 10px 12px;
    }

    .ant-descriptions-row > td {
      padding-bottom: 0;
    }

    code {
      font-size: 12px;
      font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
      background: ${PH.surfaceMuted};
      padding: 3px 8px;
      border-radius: 4px;
      color: ${PH.text};
      white-space: nowrap;
      display: inline-block;
      max-width: 100%;
      overflow-x: auto;
      vertical-align: middle;
    }
  }

  .about-steps {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .about-step {
    display: flex;
    gap: 12px;
    padding: 14px 16px;
    margin-bottom: 8px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: ${PH.surfaceMuted};
    align-items: flex-start;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &.about-step--todo {
      border-color: rgba(250, 173, 20, 0.35);
      background: #fffef9;
    }

    &.about-step--done {
      background: rgba(14, 98, 63, 0.06);
    }
  }

  .about-step-number {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    color: ${PH.textMuted};
    background: ${PH.surface};
    border: 1px solid ${PH.border};
  }

  .about-step--done .about-step-number {
    color: ${PH.success};
    background: ${PH.successBg};
    border-color: transparent;
  }

  .about-step-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-top: 0;
  }

  .step-icon {
    font-size: 16px;

    &.done {
      color: ${PH.success};
      background: ${PH.successBg};
    }

    &.todo {
      color: #d48806;
      background: rgba(250, 173, 20, 0.2);
    }

    &.later {
      color: ${PH.textMuted};
      background: rgba(0, 0, 0, 0.06);
    }
  }

  .about-step-content {
    flex: 1;
    min-width: 0;
  }

  .about-step-header {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  .about-step-title {
    font-weight: 700;
    font-size: ${PH.fontSizeBase};
    color: ${PH.text};
  }

  .step-label {
    font-size: ${PH.fontSizeSm};
    font-weight: 600;

    &.done {
      color: ${PH.success};
    }

    &.todo {
      color: #d48806;
    }

    &.later {
      color: ${PH.textMuted};
      font-weight: 500;
    }
  }

  .about-step-description {
    margin: 0 0 8px;
    font-size: ${PH.fontSizeSm};
    color: ${PH.textMuted};
    line-height: 1.55;
  }

  .about-step-cta {
    font-weight: 600;
    font-size: ${PH.fontSizeSm};

    .anticon {
      margin-left: 4px;
      font-size: 12px;
    }
  }
`;
